import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QrCode, Camera, CameraOff, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const READER_ID = 'qr-reader';

const ScanCheckIn = () => {
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState('');
  const scannerRef = useRef(null);
  const lockRef = useRef(false);

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch (e) { /* ignore */ }
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const submitToken = async (token) => {
    if (!token) return;
    setProcessing(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/checkin`, { token }, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = res.data;
      setResult({ kind: data.status === 'already' ? 'already' : 'success', ...data });
      if (data.status === 'already') toast.warning('Already checked in');
      else toast.success('Checked in!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Check-in failed';
      setResult({ kind: 'error', message: msg });
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (lockRef.current) return;
    lockRef.current = true;
    await stopScanner();
    await submitToken((decodedText || '').trim());
  };

  const startScanner = async () => {
    setResult(null);
    lockRef.current = false;
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(READER_ID);
    }
    try {
      setScanning(true);
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onScanSuccess,
        () => {}
      );
    } catch (err) {
      setScanning(false);
      toast.error('Could not start the camera. Check permissions or use manual entry.');
    }
  };

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    await stopScanner();
    await submitToken(manual.trim());
    setManual('');
  };

  const resultStyles = {
    success: 'bg-green-50 border-green-500 text-green-800',
    already: 'bg-amber-50 border-amber-500 text-amber-800',
    error: 'bg-red-50 border-red-500 text-red-800',
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="scan-checkin">
      <h2 className="text-3xl font-bold mb-2 flex items-center gap-3" style={{ fontFamily: 'var(--heading-font)' }} data-testid="scan-title">
        <QrCode size={28} /> Check-In Scanner
      </h2>
      <p className="text-zinc-600 mb-6">Scan a participant&apos;s QR code to mark them present. Check-in is only allowed on the class&apos;s scheduled date.</p>

      {/* Camera viewport */}
      <div className="bg-black/5 border border-zinc-200 p-4 mb-4">
        <div id={READER_ID} className="w-full overflow-hidden rounded" data-testid="qr-reader" style={{ minHeight: scanning ? 'auto' : '0' }} />
        {!scanning && (
          <div className="flex flex-col items-center justify-center py-10 text-zinc-400" data-testid="scanner-idle">
            <Camera size={40} className="mb-3" />
            <p className="text-sm">Camera is off</p>
          </div>
        )}
        <div className="flex gap-3 mt-4">
          {!scanning ? (
            <button
              onClick={startScanner}
              disabled={processing}
              className="flex-1 bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="start-scan-btn"
            >
              <Camera size={20} /> START SCANNER
            </button>
          ) : (
            <button
              onClick={stopScanner}
              className="flex-1 border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 font-bold transition-colors flex items-center justify-center gap-2"
              data-testid="stop-scan-btn"
            >
              <CameraOff size={20} /> STOP SCANNER
            </button>
          )}
        </div>
      </div>

      {/* Result */}
      {processing && (
        <div className="border-2 border-zinc-200 p-4 mb-4 text-center text-zinc-500" data-testid="checkin-processing">Checking in…</div>
      )}
      {result && !processing && (
        <div className={`border-2 p-5 mb-4 ${resultStyles[result.kind]}`} data-testid={`checkin-result-${result.kind}`}>
          <div className="flex items-start gap-3">
            {result.kind === 'success' && <CheckCircle2 size={28} className="shrink-0" />}
            {result.kind === 'already' && <AlertTriangle size={28} className="shrink-0" />}
            {result.kind === 'error' && <XCircle size={28} className="shrink-0" />}
            <div>
              <p className="font-bold text-lg">
                {result.kind === 'success' && 'Checked In ✓'}
                {result.kind === 'already' && 'Already Checked In'}
                {result.kind === 'error' && 'Check-In Failed'}
              </p>
              {result.kind === 'error' ? (
                <p className="text-sm mt-1" data-testid="checkin-error-message">{result.message}</p>
              ) : (
                <div className="text-sm mt-1 space-y-0.5">
                  <p data-testid="checkin-name"><strong>{result.customer_name}</strong></p>
                  <p>{result.item_name || result.booking_type} • {result.booking_date}{result.booking_time ? ` • ${result.booking_time}` : ''}</p>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={startScanner}
            className="mt-4 w-full bg-black text-white hover:bg-zinc-800 rounded-full px-6 py-2.5 font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="scan-next-btn"
          >
            <Camera size={18} /> SCAN NEXT
          </button>
        </div>
      )}

      {/* Manual entry fallback */}
      <form onSubmit={handleManual} className="bg-white border border-zinc-200 p-4" data-testid="manual-checkin-form">
        <Label htmlFor="manual-code">Manual code entry (if camera is unavailable)</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="manual-code"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Paste / type the check-in code"
            data-testid="manual-code-input"
          />
          <button
            type="submit"
            disabled={processing || !manual.trim()}
            className="bg-zinc-900 text-white rounded px-5 font-medium disabled:opacity-50"
            data-testid="manual-checkin-btn"
          >
            Check In
          </button>
        </div>
      </form>
    </div>
  );
};

export default ScanCheckIn;
